<div class="stripe-dialog">
  <div class="header-row">
    <p class="dialog-title">{{ 'Stripe Settings' | translate }}</p>
    <button class="close-icon" type="button" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Skeleton shown while the parent fetches+decrypts previously saved settings -->
  <div class="skeleton-fields" *ngIf="loading">
    <div class="field-group" *ngFor="let i of [1,2,3,4,5,6,7,8]">
      <div class="skeleton-line skeleton-sm" style="width: 35%;"></div>
      <div class="skeleton-line skeleton-lg" style="width: 100%;"></div>
    </div>
    <div class="buttons">
      <div class="skeleton-line skeleton-lg" style="width: 100%;"></div>
    </div>
  </div>

  <form [formGroup]="form" (ngSubmit)="save()" *ngIf="!loading">
    <div class="validation-banner" *ngIf="formSubmitted && form.invalid">
      {{ 'Please fix the highlighted fields' | translate }}
    </div>

    <!-- Two independent ways to accept Stripe payments — both stay fully usable.
         BYOK keeps working (and is the only option) for merchants whose business
         isn't registered in a Stripe Connect-supported country; Connect adds a
         platform commission + destination charge split for merchants who are. -->
    <div class="field-group">
      <label class="field-label">{{ 'How do you want to accept Stripe payments?' | translate }}</label>
      <div class="mode-toggle">
        <button
          type="button"
          class="mode-option"
          [class.active]="isByokMode"
          (click)="setMode('byok')">
          {{ 'Use my own API key' | translate }}
        </button>
        <button
          type="button"
          class="mode-option"
          [class.active]="isConnectMode"
          (click)="setMode('connect')">
          {{ 'Connect with Stripe' | translate }}
        </button>
      </div>
    </div>

    <!-- BYOK: classic flow — the merchant's own Stripe secret key, used directly
         against their own Stripe account. Stacks takes no cut. -->
    <ng-container *ngIf="isByokMode">
      <div class="field-group">
        <label class="field-label">{{ 'Stripe secret key' | translate }}</label>
        <input
          class="field-input"
          formControlName="stripApiKey"
          placeholder="sk_live_..."
          [class.has-error]="form.get('stripApiKey')?.invalid && (form.get('stripApiKey')?.touched || formSubmitted)" />
        <span class="field-error" *ngIf="form.get('stripApiKey')?.hasError('required') && (form.get('stripApiKey')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
      </div>
    </ng-container>

    <!-- Connect: full onboarding — destination charges, platform commission,
         requires the business to be legally registered in a supported country. -->
    <ng-container *ngIf="isConnectMode">
      <div class="field-group">
        <label class="field-label">{{ 'Account type' | translate }}</label>
        <select
          class="field-input"
          formControlName="businessType"
          [class.has-error]="form.get('businessType')?.invalid && (form.get('businessType')?.touched || formSubmitted)">
          <option value="individual">{{ 'Individual' | translate }}</option>
          <option value="company">{{ 'Company' | translate }}</option>
        </select>
      </div>

      <div class="field-group">
        <label class="field-label">{{ 'Country' | translate }}</label>
        <select
          class="field-input"
          formControlName="country"
          [class.has-error]="form.get('country')?.invalid && (form.get('country')?.touched || formSubmitted)">
          <option value="" disabled>{{ 'Select country...' | translate }}</option>
          <option *ngFor="let code of availableCountries(form.get('businessType')?.value)" [value]="code">{{ code }}</option>
        </select>
        <span class="field-error" *ngIf="form.get('country')?.hasError('required') && (form.get('country')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        <span class="field-hint" *ngIf="availableCountries(form.get('businessType')?.value).length === 0">
          {{ "Stripe Connect isn't available yet for this account type. If your business is legally registered in a supported country, try switching account type — otherwise use your own API key instead." | translate }}
        </span>
      </div>

      <!-- Individual requirements (matches Stripe's `identity.individual` object) -->
      <ng-container *ngIf="form.get('businessType')?.value === 'individual'" formGroupName="individual">
        <div class="field-group" *ngIf="isFieldRequired('givenName')">
          <label class="field-label">{{ 'First name' | translate }}</label>
          <input
            class="field-input"
            formControlName="givenName"
            placeholder="{{ 'Legal first name' | translate }}"
            [class.has-error]="form.get('individual.givenName')?.invalid && (form.get('individual.givenName')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.givenName')?.hasError('required') && (form.get('individual.givenName')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('surname')">
          <label class="field-label">{{ 'Last name' | translate }}</label>
          <input
            class="field-input"
            formControlName="surname"
            placeholder="{{ 'Legal last name' | translate }}"
            [class.has-error]="form.get('individual.surname')?.invalid && (form.get('individual.surname')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.surname')?.hasError('required') && (form.get('individual.surname')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('dateOfBirth')">
          <label class="field-label">{{ 'Date of birth' | translate }}</label>
          <input
            class="field-input"
            type="date"
            formControlName="dateOfBirth"
            [class.has-error]="form.get('individual.dateOfBirth')?.invalid && (form.get('individual.dateOfBirth')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.dateOfBirth')?.hasError('required') && (form.get('individual.dateOfBirth')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressLine1')">
          <label class="field-label">{{ 'Address line 1' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressLine1"
            placeholder="{{ 'Street address' | translate }}"
            [class.has-error]="form.get('individual.addressLine1')?.invalid && (form.get('individual.addressLine1')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.addressLine1')?.hasError('required') && (form.get('individual.addressLine1')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressCity')">
          <label class="field-label">{{ 'City' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressCity"
            placeholder="{{ 'City' | translate }}"
            [class.has-error]="form.get('individual.addressCity')?.invalid && (form.get('individual.addressCity')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.addressCity')?.hasError('required') && (form.get('individual.addressCity')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressPostalCode')">
          <label class="field-label">{{ 'Postal code' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressPostalCode"
            inputmode="numeric"
            placeholder="{{ 'Postal code' | translate }}"
            [class.has-error]="form.get('individual.addressPostalCode')?.invalid && (form.get('individual.addressPostalCode')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('individual.addressPostalCode')?.hasError('required') && (form.get('individual.addressPostalCode')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
          <span class="field-error" *ngIf="form.get('individual.addressPostalCode')?.hasError('pattern') && (form.get('individual.addressPostalCode')?.touched || formSubmitted)">{{ 'Numbers only' | translate }}</span>
        </div>
      </ng-container>

      <!-- Company requirements (matches Stripe's `identity.business_details` object) -->
      <ng-container *ngIf="form.get('businessType')?.value === 'company'" formGroupName="business">
        <div class="field-group" *ngIf="isFieldRequired('registeredName')">
          <label class="field-label">{{ 'Company name' | translate }}</label>
          <input
            class="field-input"
            formControlName="registeredName"
            placeholder="{{ 'Registered company name' | translate }}"
            [class.has-error]="form.get('business.registeredName')?.invalid && (form.get('business.registeredName')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('business.registeredName')?.hasError('required') && (form.get('business.registeredName')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressLine1')">
          <label class="field-label">{{ 'Address line 1' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressLine1"
            placeholder="{{ 'Registered street address' | translate }}"
            [class.has-error]="form.get('business.addressLine1')?.invalid && (form.get('business.addressLine1')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('business.addressLine1')?.hasError('required') && (form.get('business.addressLine1')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressCity')">
          <label class="field-label">{{ 'City' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressCity"
            placeholder="{{ 'City' | translate }}"
            [class.has-error]="form.get('business.addressCity')?.invalid && (form.get('business.addressCity')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('business.addressCity')?.hasError('required') && (form.get('business.addressCity')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
        </div>

        <div class="field-group" *ngIf="isFieldRequired('addressPostalCode')">
          <label class="field-label">{{ 'Postal code' | translate }}</label>
          <input
            class="field-input"
            formControlName="addressPostalCode"
            inputmode="numeric"
            placeholder="{{ 'Postal code' | translate }}"
            [class.has-error]="form.get('business.addressPostalCode')?.invalid && (form.get('business.addressPostalCode')?.touched || formSubmitted)" />
          <span class="field-error" *ngIf="form.get('business.addressPostalCode')?.hasError('required') && (form.get('business.addressPostalCode')?.touched || formSubmitted)">{{ 'Field required' | translate }}</span>
          <span class="field-error" *ngIf="form.get('business.addressPostalCode')?.hasError('pattern') && (form.get('business.addressPostalCode')?.touched || formSubmitted)">{{ 'Numbers only' | translate }}</span>
        </div>
      </ng-container>
    </ng-container>

    <div class="buttons">
      <button class="save-btn" type="submit">{{ 'Save' | translate }}</button>
      <button class="disable-btn" type="button" *ngIf="data.isEnabled" (click)="disable()">{{ 'Disable Stripe' | translate }}</button>
    </div>
  </form>
</div>
